
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  subnav: true,
  maxTocLevel: 4,
  layout: 'reference',
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for endpoints to link accounts for the Transfer product.',
  source: 'endpoint'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Transfer' subtitle='API reference for Transfer account linking endpoints' compact mdxType="Header" />
    <p>{`For how-to guidance, see the `}<a parentName="p" {...{
        "href": "/docs/transfer"
      }}>{`Transfer documentation`}</a>{`.`}</p>
    <h4 {...{
      "id": "account-linking"
    }}>{`Account Linking`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Account Linking`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/transfer/capabilities/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Determine RTP and RfP eligibility for a Plaid Item`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/transfer/intent/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create a transfer intent and invoke Transfer UI (Transfer UI only)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/transfer/intent/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Retrieve information about a transfer intent (Transfer UI only)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/transfer/migrate_account`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create an Item to use with Transfer from known account and routing numbers`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "transfercapabilitiesget"
    }}>{`/transfer/capabilities/get`}</h4>
    <Schema method='POST' route='/transfer/capabilities/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/transfer/capabilities/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: TransferCapabilitiesGetRequest = {
  access_token: 'access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175',
  account_id: '3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr',
};

try {
  const response = await client.transferCapabilitiesGet(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/transfer/capabilities/get \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "access_token": "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
   "account_id": "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr"
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::TransferCapabilitiesGetRequest.new(
  {
    access_token: "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
    account_id: "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
  }
)
response = client.transfer_capabilities_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`TransferCapabilitiesGetRequest request = new TransferCapabilitiesGetRequest()
  .accessToken("access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175")
  .accountId("3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr");
Response<TransferCapabilitiesGetResponse> response = client()
  .transferCapabilitiesGet(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = TransferCapabilitiesGetRequest(
    access_token='access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175',
    account_id='3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr',
)
response = client.transfer_capabilities_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewTransferCapabilitiesGetRequest(
  "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
  "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
)

response, _, err := client.PlaidApi.TransferCapabilitiesGet(ctx).TransferCapabilitiesGetRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    transfer "github.com/plaid/plaid-go/v49/transfer"
)

request := &transfer.TransferCapabilitiesGetRequest{
    AccessToken: "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
    AccountID:   "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
}

response, err := client.Transfer.Capabilities.Get(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/transfer/capabilities/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "transferintentcreate"
    }}>{`/transfer/intent/create`}</h4>
    <Schema method='POST' route='/transfer/intent/create' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/transfer/intent/create' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: TransferIntentCreateRequest = {
  account_id: '3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr',
  mode: 'PAYMENT',
  amount: '12.34',
  description: 'Desc',
  ach_class: 'ppd',
  funding_account_id: '9853defc-e703-463d-86b1-dc0607a45359',
  user: {
    legal_name: 'Anne Charleston',
  },
};

try {
  const response = await client.transferIntentCreate(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/transfer/intent/create \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "account_id": "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
   "mode": "PAYMENT",
   "amount": "12.34",
   "description": "Desc",
   "ach_class": "ppd",
   "funding_account_id": "9853defc-e703-463d-86b1-dc0607a45359",
   "user": {
     "legal_name": "Anne Charleston"
   }
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::TransferIntentCreateRequest.new(
  {
    account_id: '3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr',
    mode: 'PAYMENT',
    amount: '12.34',
    ach_class: 'ppd',
    description: 'Desc',
    funding_account_id: '9853defc-e703-463d-86b1-dc0607a45359',
    user: {
      legal_name: 'Anne Charleston'
    }
  }
)
response = client.transfer_intent_create(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`TransferUserInRequest user = new TransferUserInRequest().legalName("Anne Charleston");
TransferIntentCreateRequest request = new TransferIntentCreateRequest()
  .accountId("3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr")
  .mode(TransferIntentCreateMode.PAYMENT)
  .amount("12.34")
  .achClass(ACHClass.PPD)
  .description("Desc")
  .fundingAccountId("9853defc-e703-463d-86b1-dc0607a45359")
  .user(user);
Response<TransferIntentCreateResponse> response = client()
  .transferIntentCreate(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = TransferIntentCreateRequest(
    account_id='3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr',
    mode=TransferIntentCreateMode('PAYMENT'),
    amount='12.34',
    ach_class=ACHClass('ppd'),
    description='Foobar',
    funding_account_id='9853defc-e703-463d-86b1-dc0607a45359',
    user=TransferUserInRequest(legal_name='Anne Charleston')
)
response = client.transfer_intent_create(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewTransferIntentCreateRequest(
  "PAYMENT",
  "12.34",
  "Desc",
  *plaid.NewTransferUserInRequest("Anne Charleston"),
)
request.SetAchClass("ppd")
request.SetAccountId("3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr")
request.SetFundingAccountId("9853defc-e703-463d-86b1-dc0607a45359")

response, _, err := client.PlaidApi.TransferIntentCreate(ctx).TransferIntentCreateRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    transfer "github.com/plaid/plaid-go/v49/transfer"
)

request := &transfer.TransferIntentCreateRequest{
    Mode:        "PAYMENT",
    Amount:      "12.34",
    Description: "Desc",
    User:        &plaid.TransferUserInRequest{LegalName: "Anne Charleston"},
}
request.AchClass = plaid.AchClass("ppd").Ptr()
request.AccountID = plaid.String("3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr")
request.FundingAccountID = plaid.String("9853defc-e703-463d-86b1-dc0607a45359")

response, err := client.Transfer.Intent.Create(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/transfer/intent/create' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "transferintentget"
    }}>{`/transfer/intent/get`}</h4>
    <Schema method='POST' route='/transfer/intent/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/transfer/intent/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: TransferIntentGetRequest = {
  transfer_intent_id: '460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9',
};

try {
  const response = await client.transferIntentGet(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/transfer/intent/get \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "transfer_intent_id": "460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9"
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::TransferIntentGetRequest.new(
  {
    transfer_intent_id: '460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9'
  }
)
response = client.transfer_intent_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`TransferIntentGetRequest request = new TransferIntentGetRequest()
  .transferIntentId("460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9");

Response<TransferIntentGetResponse> response = client()
  .transferIntentGet(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = TransferIntentGetRequest(
    transfer_intent_id='460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9'
)
response = client.transfer_intent_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewTransferIntentGetRequest(
  "460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9",
)

response, _, err := client.PlaidApi.TransferIntentGet(ctx).TransferIntentGetRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    transfer "github.com/plaid/plaid-go/v49/transfer"
)

request := &transfer.TransferIntentGetRequest{TransferIntentID: "460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9"}

response, err := client.Transfer.Intent.Get(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/transfer/intent/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "transfermigrate_account"
    }}>{`/transfer/migrate_account`}</h4>
    <Schema method='POST' route='/transfer/migrate_account' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/transfer/migrate_account' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: TransferMigrateAccountRequest = {
  account_number: '100000000',
  routing_number: '121122676',
  account_type: 'checking',
};
try {
  const response = await plaidClient.transferMigrateAccount(request);
  const access_token = response.data.access_token;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/transfer/migrate_account \\
 -H 'content-type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "account_number": String,
   "routing_number": String,
   "account_type": String
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::TransferMigrateAccountRequest.new(
  {
    account_number: "100000000",
    routing_number: "121122676",
    account_type: "checking",
  }
)
response = client.transfer_migrate_account(request)
access_token = response.access_token
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`TransferMigrateAccountRequest request = new TransferMigrateAccountRequest()
  .accountNumber("100000000")
  .routingNumber("121122676")
  .accountType("checking");
Response<TransferMigrateAccountResponse> response = client()
  .transferMigrateAccount(request)
  .execute();
String accessToken = response.body().getAccessToken();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = TransferMigrateAccountRequest(
    account_number = '100000000',
    routing_number = '121122676',
    account_type = 'checking'
)
response = client.transfer_migrate_account(request)
access_token = response['access_token']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewTransferMigrateAccountRequest(
  "ACCOUNT_NUMBER", // 100000000
  "ROUTING_NUMBER", // 121122676
  "ACCOUNT_TYPE", // checking
)
response, _, err := client.PlaidApi.TransferMigrateAccount(ctx).TransferMigrateAccountRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.TransferMigrateAccountRequest{
    AccountNumber: "ACCOUNT_NUMBER", // 100000000

    RoutingNumber: "ROUTING_NUMBER", // 121122676

    AccountType: "ACCOUNT_TYPE", // checking
}
response, err := client.Transfer.MigrateAccount(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/transfer/migrate_account' type='response' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "account-linking",
        level: 4,
        title: "Account Linking",
      },
{
        id: "transfercapabilitiesget",
        level: 4,
        title: "/transfer/capabilities/get",
      },
{
        id: "transferintentcreate",
        level: 4,
        title: "/transfer/intent/create",
      },
{
        id: "transferintentget",
        level: 4,
        title: "/transfer/intent/get",
      },
{
        id: "transfermigrate_account",
        level: 4,
        title: "/transfer/migrate_account",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
